import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Mail, Send, RefreshCw, Unlink, RotateCw, Search, PlusCircle } from 'lucide-react';
import api from './api/axios';
import Pagination from './components/Pagination';

// Maps Plaid error_code -> human label + remediation hint, grouped by bucket.
const BROKEN_REASON_LABELS = {
  // Bucket 1 — Restore link (update mode / re-authenticate)
  ITEM_LOGIN_REQUIRED:   { label: 'Login required',      hint: 'Re-authenticate — send restore link', action: 'restore' },
  PENDING_EXPIRATION:    { label: 'Pending expiration',  hint: 'Re-authenticate — send restore link', action: 'restore' },
  PENDING_DISCONNECT:    { label: 'Pending disconnect',  hint: 'Re-authenticate — send restore link', action: 'restore' },
  INVALID_CREDENTIALS:   { label: 'Invalid credentials', hint: 'Re-authenticate — send restore link', action: 'restore' },
  INVALID_MFA:           { label: 'Invalid MFA',         hint: 'Re-authenticate — send restore link', action: 'restore' },
  ACCESS_NOT_GRANTED:    { label: 'Access not granted',  hint: 'Re-authenticate — send restore link', action: 'restore' },
  // Bucket 2 — New access link (accounts unavailable)
  NO_ACCOUNTS:                     { label: 'No accounts',              hint: 'Accounts unavailable — send new access link', action: 'new_link' },
  ITEM_NOT_SUPPORTED:              { label: 'Item not supported',       hint: 'Accounts unavailable — send new access link', action: 'new_link' },
  INSTITUTION_NO_LONGER_SUPPORTED: { label: 'Institution unsupported',  hint: 'Accounts unavailable — send new access link', action: 'new_link' },
  MFA_NOT_SUPPORTED:               { label: 'MFA not supported',        hint: 'Accounts unavailable — send new access link', action: 'new_link' },
  PRODUCTS_NOT_SUPPORTED:          { label: 'Products not supported',   hint: 'Accounts unavailable — send new access link', action: 'new_link' },
  // Bucket 3 — Bank action first
  ITEM_LOCKED:            { label: 'Item locked',           hint: 'User must act at their bank, then re-link', action: 'new_link' },
  PASSWORD_RESET_REQUIRED:{ label: 'Password reset needed', hint: 'User must act at their bank, then re-link', action: 'new_link' },
  USER_SETUP_REQUIRED:    { label: 'User setup required',   hint: 'User must act at their bank, then re-link', action: 'new_link' },
  // Bucket 4 — Linked rows flagged for attention (link_status stays 'Linked')
  NOT_SYNCING:              { label: 'Not syncing',       hint: 'No successful sync in 3 days — send restore link or new access link', action: 'restore' },
  USER_PERMISSION_REVOKED:  { label: 'Permission revoked', hint: 'Client revoked access at the bank — send new access link', action: 'new_link' },
  // restore (update mode), never new_link: a fresh Link session at Chase/PNC/Schwab replaces the still-live Item's consent
  USER_ACCOUNT_REVOKED:     { label: 'Account revoked',    hint: 'Client revoked an account at the bank — send restore link to re-add it', action: 'restore' },
  // Set by the webhook handler when saving an exchanged Item failed. EXCHANGE_FAILED: no Item exists, a new link is right.
  // FORK_COMMIT_FAILED: the original Item is live — a new Link session would replace its consent at Chase/PNC/Schwab.
  EXCHANGE_FAILED:          { label: 'Link save failed',   hint: 'Bank connection could not be saved after linking — send new access link', action: 'new_link' },
  FORK_COMMIT_FAILED:       { label: 'Duplicate link failed', hint: 'Original connection is live — contact engineering before re-linking', action: 'restore' },
};

// Resolves a broken_reason code to display values, with a fallback for unknown/null.
// Unknown/null codes default to the 'new_link' remediation bucket.
const resolveBrokenReason = (code) => {
  if (code && BROKEN_REASON_LABELS[code]) return BROKEN_REASON_LABELS[code];
  return { label: code || 'Unknown', hint: 'Review / re-link', action: 'new_link' };
};

// Resolves the display + action for a "links needing attention" panel row.
// NOT_SYNCING's action depends on whether the row still has an access token
// (restore reuses it; otherwise a brand new access link is required).
const resolvePanelLinkAction = (link) => {
  const resolved = resolveBrokenReason(link.broken_reason);
  if (link.broken_reason === 'NOT_SYNCING') {
    return { ...resolved, action: link.has_access_token ? 'restore' : 'new_link' };
  }
  return resolved;
};

// Formats a UTC-marked ISO string to US Eastern time; returns an em dash for null/undefined.
const formatEastern = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  }) + ' ET';
};

const RequestAccess = () => {
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    lawFirm: '',
    email: ''
  });
  
  // Customers state
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    total_records: 0,
    total_pages: 1,
    current_page: 1,
    page_size: 10,
    has_next: false,
    has_previous: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Track which customer is expanded - store the email since it's our primary key
  const [expandedCustomerEmail, setExpandedCustomerEmail] = useState(null);
  
  // State for tracking which customer is being processed for sending a link
  const [sendingLinkToCustomer, setSendingLinkToCustomer] = useState(null);
  
  // State for confirmation dialogs
  const [showCustomerConfirmation, setShowCustomerConfirmation] = useState(false);
  const [customerToSendLink, setCustomerToSendLink] = useState(null);
  
  // State for unlink operations
  const [unlinkingHistoryId, setUnlinkingHistoryId] = useState(null);
  const [showUnlinkConfirmation, setShowUnlinkConfirmation] = useState(false);
  const [historyToUnlink, setHistoryToUnlink] = useState(null);
  
  // State for restore operations
  const [showRestoreConfirmation, setShowRestoreConfirmation] = useState(false);
  const [historyToRestore, setHistoryToRestore] = useState(null);
  const [restoringHistoryId, setRestoringHistoryId] = useState(null);

  // State for sending a new access link from a broken card
  const [showNewLinkConfirmation, setShowNewLinkConfirmation] = useState(false);
  const [brokenLinkToReissue, setBrokenLinkToReissue] = useState(null);
  const [reissuingBrokenLinkId, setReissuingBrokenLinkId] = useState(null);

  // State for the "Add accounts (same bank)" flow on a Linked row
  const [showAddAccountsConfirmation, setShowAddAccountsConfirmation] = useState(false);
  const [historyToAddAccounts, setHistoryToAddAccounts] = useState(null);
  const [addingAccountsHistoryId, setAddingAccountsHistoryId] = useState(null);

  // Broken links state (fetched from dedicated endpoint)
  const [brokenLinks, setBrokenLinks] = useState([]);
  const [brokenLinksLoading, setBrokenLinksLoading] = useState(false);

  // Fetch broken links from dedicated endpoint
  const fetchBrokenLinks = async () => {
    setBrokenLinksLoading(true);
    try {
      const response = await api.get('/api/customers/broken-links');
      setBrokenLinks(response.data);
    } catch (err) {
      console.error('Error fetching broken links:', err);
    } finally {
      setBrokenLinksLoading(false);
    }
  };

  // Toggle customer history expansion - only one expanded at a time
  const toggleCustomerHistory = (customerEmail) => {
    if (expandedCustomerEmail === customerEmail) {
      // If clicking the already expanded customer, collapse it
      setExpandedCustomerEmail(null);
    } else {
      // Otherwise, expand this customer and collapse any others
      setExpandedCustomerEmail(customerEmail);
    }
  };
  
  // Function to refresh the customer history
  const refreshCustomerHistory = async (page = currentPage, size = pageSize, search = searchTerm) => {
    setLoading(true);
    try {
      const params = {
        page: page,
        page_size: size
      };
      
      if (search && search.trim()) {
        params.search = search.trim();
      }
      
      const response = await api.get('/api/customers/history', { params });
      
      if (response.data && response.data.data && response.data.pagination) {
        setCustomers(response.data.data);
        setPagination(response.data.pagination);
        setCurrentPage(response.data.pagination.current_page);
      } else {
        console.error('Unexpected response format:', response.data);
        setError('Unexpected response format from server.');
      }
    } catch (err) {
      console.error('Error refreshing customer data:', err);
      setError('Failed to refresh customer data.');
    } finally {
      setLoading(false);
    }
  };
  
  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    refreshCustomerHistory(page, pageSize, searchTerm);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
    refreshCustomerHistory(1, size, searchTerm);
  };

  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setCurrentPage(1); // Reset to first page when searching
    
    // Debounce search to avoid too many API calls
    if (handleSearchChange.timeoutId) {
      clearTimeout(handleSearchChange.timeoutId);
    }
    handleSearchChange.timeoutId = setTimeout(() => {
      refreshCustomerHistory(1, pageSize, newSearchTerm);
    }, 300);
  };

  // Fetch customer history data and broken links from API on component mount
  useEffect(() => {
    refreshCustomerHistory();
    fetchBrokenLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: refreshCustomerHistory/fetchBrokenLinks are redefined each render
  }, []);
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Function to show confirmation before sending link
  const showSendLinkConfirmation = (customer) => {
    setCustomerToSendLink(customer);
    setShowCustomerConfirmation(true);
  };
  
  // Function to show confirmation before unlinking
  const showUnlinkConfirmationDialog = (historyRecord, customer) => {
    setHistoryToUnlink({ ...historyRecord, customer });
    setShowUnlinkConfirmation(true);
  };
  
  // Function to show confirmation before restoring
  const showRestoreConfirmationDialog = (historyRecord, customer) => {
    setHistoryToRestore({ ...historyRecord, customer });
    setShowRestoreConfirmation(true);
  };

  // Function to show confirmation before sending a new access link from a broken card
  const showNewLinkConfirmationDialog = (link) => {
    setBrokenLinkToReissue(link);
    setShowNewLinkConfirmation(true);
  };

  // Function to show confirmation before sending an add-accounts (same bank) link
  const showAddAccountsConfirmationDialog = (historyRecord, customer) => {
    setHistoryToAddAccounts({ ...historyRecord, customer });
    setShowAddAccountsConfirmation(true);
  };

  // Function to unlink an account
  const unlinkAccount = async (linkHistoryId) => {
    setUnlinkingHistoryId(linkHistoryId);
    
    try {
      await api.post(`/api/utility/unlink-account/${linkHistoryId}`);
      
      // Refresh the customer history and broken links to show the updated status
      await refreshCustomerHistory();
      await fetchBrokenLinks();

      // Show success message (could be enhanced with a toast notification)
      console.log('Account successfully unlinked');
      
    } catch (err) {
      console.error('Error unlinking account:', err);
      
      if (err.response) {
        alert(`Failed to unlink account: ${err.response.data.detail || 'Server error'}`);
      } else {
        alert('Failed to unlink account. Please try again.');
      }
    } finally {
      setUnlinkingHistoryId(null);
      setShowUnlinkConfirmation(false);
      setHistoryToUnlink(null);
    }
  };
  
  // Function to restore a broken account
  const restoreAccount = async (historyRecord, customer) => {
    setRestoringHistoryId(historyRecord.id);
    
    try {
      await api.post('/api/email/send-restore-link', {
        link_history_id: historyRecord.id
      });

      // Close the confirmation modal immediately on success to prevent a
      // double-submit during the awaited refreshes below.
      setShowRestoreConfirmation(false);
      setHistoryToRestore(null);

      // Refresh customer list and broken links to show updated status
      await refreshCustomerHistory();
      await fetchBrokenLinks();

    } catch (err) {
      console.error('Error sending restore link:', err);
      if (err.response) {
        alert(`Failed to send restore link: ${err.response.data.detail || 'Server error'}`);
      } else {
        alert('Failed to send restore link. Please try again.');
      }
    } finally {
      setRestoringHistoryId(null);
    }
  };

  // Function to send an add-accounts (same bank, update mode) link for a Linked row
  const addAccountsForCustomer = async (historyRecord) => {
    setAddingAccountsHistoryId(historyRecord.id);

    try {
      await api.post('/api/email/send-add-accounts-link', {
        link_history_id: historyRecord.id
      });

      // Close the confirmation modal immediately on success to prevent a
      // double-submit during the awaited refreshes below.
      setShowAddAccountsConfirmation(false);
      setHistoryToAddAccounts(null);

      // Refresh customer list to reflect the updated "Sent" state on the row
      await refreshCustomerHistory();
      await fetchBrokenLinks();

    } catch (err) {
      console.error('Error sending add-accounts link:', err);
      if (err.response) {
        alert(`Failed to send add-accounts link: ${err.response.data.detail || 'Server error'}`);
      } else {
        alert('Failed to send add-accounts link. Please try again.');
      }
    } finally {
      setAddingAccountsHistoryId(null);
    }
  };

  // Function to send a fresh access link for a broken card (new_link remediation bucket).
  // Reuses /api/email/send-access-link with the broken card's existing customer info.
  const sendNewAccessLinkForBrokenLink = async (link) => {
    setReissuingBrokenLinkId(link.id);

    try {
      await api.post('/api/email/send-access-link', {
        firstName: link.customer_first_name,
        lastName: link.customer_last_name,
        lawFirm: link.law_firm,
        email: link.customer_email
      });

      // Close the confirmation modal immediately on success to prevent a
      // double-submit during the awaited refreshes below.
      setShowNewLinkConfirmation(false);
      setBrokenLinkToReissue(null);

      // Refresh broken links and customer history to reflect the new "Sent" link
      await fetchBrokenLinks();
      await refreshCustomerHistory();

    } catch (err) {
      console.error('Error sending new access link:', err);
      if (err.response) {
        alert(`Failed to send new access link: ${err.response.data.detail || `Server error: ${err.response.status}`}`);
      } else if (err.request) {
        alert('Failed to send new access link. No response from server.');
      } else {
        alert(`Failed to send new access link: ${err.message}`);
      }
    } finally {
      setReissuingBrokenLinkId(null);
    }
  };

  // Function to send a link to an existing customer
  const sendLinkToExistingCustomer = async (customer) => {
    if (sendingLinkToCustomer === customer.email) {
      return; // Already processing this customer
    }
    
    // Set the customer being processed
    setSendingLinkToCustomer(customer.email);
    
    try {
      // Prepare the data from the existing customer
      const customerData = {
        firstName: customer.firstName,
        lastName: customer.lastName,
        lawFirm: customer.lawFirm,
        email: customer.email
      };
      
      // Use the same endpoint as the form
      await api.post('/api/email/send-access-link', customerData);
      
      // Show a temporary success message (we'll handle this visually in the UI)
      setTimeout(() => {
        // Refresh customer list to show the updated link history
        refreshCustomerHistory();
      }, 2000);
      
    } catch (err) {
      console.error('Error sending link to existing customer:', err);
      
      // Handle error with more detailed reporting
      if (err.response) {
        alert(`Failed to send link. Server error: ${err.response.status}`);
      } else if (err.request) {
        alert('Failed to send link. No response from server.');
      } else {
        alert(`Failed to send link: ${err.message}`);
      }
    } finally {
      // Reset sending state after a short delay
      setTimeout(() => {
        setSendingLinkToCustomer(null);
      }, 2000);
    }
  };
  
  // Handle form submission with API call
  const handleSendLink = async (e) => {
    e.preventDefault();
    setSendingLink(true);
    setLinkSent(false);
    
    try {
      // Send access link request to backend API
      await api.post('/api/email/send-access-link', formData);

      // Reset form after successful submission
      setFormData({
        firstName: '',
        lastName: '',
        lawFirm: '',
        email: ''
      });
      
      setLinkSent(true);
      
      // Automatically hide the success message after 2 seconds
      setTimeout(() => {
        setLinkSent(false);
      }, 2000);
      
      // Refresh customer list to show the new link
      refreshCustomerHistory();
      
    } catch (err) {
      console.error('Error sending access link:', err);
      
      // More detailed error logging
      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Response data:', err.response.data);
        console.error('Response status:', err.response.status);
        console.error('Response headers:', err.response.headers);
        alert(`Failed to send access link. Server error: ${err.response.status}. Please try again.`);
      } else if (err.request) {
        // The request was made but no response was received
        console.error('No response received:', err.request);
        alert('Failed to send access link. No response from server. Please check your connection and try again.');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error setting up request:', err.message);
        alert(`Failed to send access link: ${err.message}. Please try again.`);
      }
    } finally {
      setSendingLink(false);
    }
  };
  
  return (
    <div className="space-y-5">
      {/* Top Section: Form and Broken Links side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Request Access Form - Left Side */}
        <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Request Access</h2>
        
        <form onSubmit={handleSendLink} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="lawFirm" className="block text-sm font-medium text-gray-700 mb-1">
              Law Firm Name *
            </label>
            <input
              type="text"
              id="lawFirm"
              name="lawFirm"
              value={formData.lawFirm}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
          </div>
          
          <div className="pt-2 flex justify-end">
            <button
              type="submit"
              disabled={sendingLink}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-full text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-all duration-200"
            >
              {sendingLink ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Link
                </>
              )}
            </button>
          </div>
          
          {linkSent && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-700 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Access link sent successfully!
              </p>
            </div>
          )}
        </form>
        </div>
        
        {/* Links Needing Attention Section - Right Side */}
        <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <RefreshCw className="w-5 h-5 mr-2 text-amber-500" />
            Links needing attention
          </h2>

          {brokenLinksLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-500"></div>
              <span className="ml-2 text-gray-500">Loading links needing attention...</span>
            </div>
          ) : brokenLinks.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-50 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <p className="text-gray-500">No links needing attention found</p>
              <p className="text-sm text-gray-400 mt-1">All accounts are properly connected and syncing</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-80 overflow-y-auto">
              <div className="text-sm text-gray-600 mb-4">
                Found {brokenLinks.length} link{brokenLinks.length !== 1 ? 's' : ''} needing attention
              </div>

              {brokenLinks.map((link) => {
                const isLinked = link.link_status === 'Linked';
                const panelAction = resolvePanelLinkAction(link);
                return (
                <div key={link.id} className={`border rounded-lg p-4 ${isLinked ? 'border-amber-200 bg-amber-50' : 'border-red-200 bg-red-50'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isLinked ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                          {isLinked ? (link.broken_reason === 'NOT_SYNCING' ? 'Not syncing' : 'Linked') : 'Broken'}
                        </span>
                        {link.broken_reason && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            {panelAction.label}
                          </span>
                        )}
                      </div>
                      <div className="mb-2">
                        <p className="font-medium text-gray-900">
                          {link.customer_first_name} {link.customer_last_name}
                        </p>
                        <p className="text-sm text-gray-600">{link.customer_email}</p>
                        <p className="text-sm text-gray-600">{link.law_firm}</p>
                        {link.institution_name && (
                          <p className="text-sm text-gray-600">{link.institution_name}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">Linked: {formatEastern(link.linked_at)}</p>
                        <p className="text-xs text-gray-500">Broken: {formatEastern(link.broken_at)}</p>
                      </div>
                      {link.broken_reason && (
                        <p className="text-xs text-amber-700">
                          {panelAction.hint}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 justify-center">
                      {panelAction.action === 'restore' ? (
                        <button
                          onClick={() => showRestoreConfirmationDialog(
                            { id: link.id },
                            { firstName: link.customer_first_name, lastName: link.customer_last_name, email: link.customer_email, lawFirm: link.law_firm }
                          )}
                          disabled={restoringHistoryId === link.id}
                          className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full transition-all duration-200 ${
                            restoringHistoryId === link.id
                              ? 'bg-orange-200 text-orange-700 cursor-not-allowed'
                              : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                          }`}
                        >
                          {restoringHistoryId === link.id ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-orange-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Sending...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Restore
                            </>
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => showNewLinkConfirmationDialog(link)}
                          disabled={reissuingBrokenLinkId === link.id}
                          className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full transition-all duration-200 ${
                            reissuingBrokenLinkId === link.id
                              ? 'bg-blue-200 text-blue-700 cursor-not-allowed'
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                        >
                          {reissuingBrokenLinkId === link.id ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-blue-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-3 h-3 mr-1" />
                              Send new access link
                            </>
                          )}
                        </button>
                      )}
                      {!isLinked && (
                        <button
                          onClick={() => showUnlinkConfirmationDialog(
                            { id: link.id },
                            { firstName: link.customer_first_name, lastName: link.customer_last_name, email: link.customer_email, lawFirm: link.law_firm }
                          )}
                          disabled={unlinkingHistoryId === link.id}
                          className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full transition-all duration-200 ${
                            unlinkingHistoryId === link.id
                              ? 'bg-red-200 text-red-700 cursor-not-allowed'
                              : 'bg-red-100 text-red-700 hover:bg-red-200'
                          }`}
                        >
                          {unlinkingHistoryId === link.id ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Unlinking...
                            </>
                          ) : (
                            <>
                              <Unlink className="w-3 h-3 mr-1" />
                              Unlink
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      {/* Customer History */}
      <div className="bg-white rounded-3xl shadow-md border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
          <div className="flex items-center">
            <h2 className="text-lg font-semibold text-gray-800">Request Access History</h2>
            <button
              onClick={() => refreshCustomerHistory()}
              disabled={loading}
              className={`ml-3 p-1 rounded-full ${loading ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-500 hover:bg-blue-100'}`}
              title="Refresh data"
            >
              <RotateCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          
          {/* Search Bar */}
          <div className="relative w-full sm:w-96">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by name, email, or law firm"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            {searchTerm && (
              <button
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => {
                  setSearchTerm('');
                  setCurrentPage(1);
                  refreshCustomerHistory(1, pageSize, '');
                }}
              >
                <span className="text-gray-400 hover:text-gray-500">✕</span>
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          {searchTerm && (
            <div className="px-6 py-2 bg-gray-50 border-b border-gray-200 text-sm text-gray-500">
              Showing results for "{searchTerm}" - {pagination.total_records} {pagination.total_records === 1 ? 'customer' : 'customers'} found
            </div>
          )}
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <span className="ml-2 text-gray-500">Loading...</span>
          </div>
        ) : error ? (
          <div className="p-4 bg-red-50 text-red-700 rounded-md">
            {error}
          </div>
        ) : customers.length === 0 ? (
          <div className="p-4 text-gray-500 text-center">
            No customer history found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/80">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Law Firm
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Links Sent
                  </th>
                  <th scope="col" className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {customers.map(customer => (
                  <React.Fragment key={customer.email}>
                    <tr className="hover:bg-blue-50/50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {customer.firstName} {customer.lastName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {customer.email}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {customer.lawFirm}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {customer.history.length}
                        <button
                          onClick={() => toggleCustomerHistory(customer.email)}
                          className="ml-2 text-blue-600 hover:text-blue-900 inline-flex items-center text-xs"
                        >
                          {expandedCustomerEmail === customer.email ? (
                            <>
                              Hide <ChevronUp className="w-3 h-3 ml-1" />
                            </>
                          ) : (
                            <>
                              View <ChevronDown className="w-3 h-3 ml-1" />
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <button
                          onClick={() => showSendLinkConfirmation(customer)}
                          disabled={sendingLinkToCustomer === customer.email}
                          className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-full transition-all duration-200 ${
                            sendingLinkToCustomer === customer.email
                              ? 'bg-blue-200 text-blue-700 cursor-not-allowed'
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                        >
                          {sendingLinkToCustomer === customer.email ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-blue-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-3 h-3 mr-1" />
                              Send Link
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                    
                    {/* Expandable History */}
                    {expandedCustomerEmail === customer.email && (
                      <tr className="bg-gray-50">
                        <td colSpan="5" className="px-4 py-3">
                          <div className="border rounded-xl overflow-hidden">
                            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                              <h3 className="text-sm font-medium text-gray-700">Access Request History for {customer.firstName} {customer.lastName}</h3>
                            </div>
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-100/80">
                                <tr>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Date
                                  </th>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Status
                                  </th>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Sent By
                                  </th>
                                  <th scope="col" className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {customer.history.map(record => (
                                  <tr key={record.id} className="hover:bg-blue-50/50">
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                      {new Date(record.date).toLocaleDateString()}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-medium rounded-full
                                        ${record.status === 'Sent' ? 'bg-blue-100 text-blue-800' :
                                          record.status === 'Linked' ? 'bg-green-100 text-green-800' :
                                          record.status === 'Broken' ? 'bg-red-100 text-red-800' :
                                          record.status === 'Unlinked' ? 'bg-gray-100 text-gray-800' :
                                          record.status === 'Expired' ? 'bg-yellow-100 text-yellow-800' :
                                          'bg-red-100 text-red-800'}`}>
                                        {record.status}
                                      </span>
                                      {record.status === 'Broken' && record.broken_reason && (
                                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                                              title={resolveBrokenReason(record.broken_reason).hint}>
                                          {resolveBrokenReason(record.broken_reason).label}
                                        </span>
                                      )}
                                      {record.institution_name && (
                                        <span
                                          className="ml-2 inline-block max-w-[8rem] truncate align-bottom text-xs text-gray-700"
                                          title={record.institution_name}
                                        >
                                          {record.institution_name}
                                        </span>
                                      )}
                                      {record.status === 'Linked' && record.item_id_suffix && (
                                        <span className="ml-2 text-xs text-gray-400">
                                          Item …{record.item_id_suffix}
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      {record.sender && record.sender.full_name ? (
                                        <div className="flex items-center">
                                          <div className="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                                            <span className="text-sm font-medium text-blue-800">
                                              {record.sender.full_name.split(' ').map(name => name[0]).join('')}
                                            </span>
                                          </div>
                                          <div className="ml-3">
                                            <div className="text-sm font-medium text-gray-900">{record.sender.full_name}</div>
                                            <div className="text-xs text-gray-500">{record.sender.email}</div>
                                          </div>
                                        </div>
                                      ) : (
                                        <span className="text-sm text-gray-500">Unknown</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-right">
                                      {record.status === 'Linked' && (
                                        <div className="flex gap-2 justify-end">
                                          {record.has_access_token && (
                                            <button
                                              onClick={() => showAddAccountsConfirmationDialog(record, customer)}
                                              disabled={addingAccountsHistoryId === record.id}
                                              title="Send a link to add more accounts at the same bank. For a different bank, use Send new access link."
                                              className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-full transition-all duration-200 ${
                                                addingAccountsHistoryId === record.id
                                                  ? 'bg-blue-200 text-blue-700 cursor-not-allowed'
                                                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                              }`}
                                            >
                                              {addingAccountsHistoryId === record.id ? (
                                                <>
                                                  <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-blue-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                  </svg>
                                                  Sending...
                                                </>
                                              ) : (
                                                <>
                                                  <PlusCircle className="w-3 h-3 mr-1" />
                                                  Add accounts (same bank)
                                                </>
                                              )}
                                            </button>
                                          )}
                                          <button
                                            onClick={() => showUnlinkConfirmationDialog(record, customer)}
                                            disabled={unlinkingHistoryId === record.id}
                                            className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-full transition-all duration-200 ${
                                              unlinkingHistoryId === record.id
                                                ? 'bg-red-200 text-red-700 cursor-not-allowed'
                                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                                            }`}
                                          >
                                            {unlinkingHistoryId === record.id ? (
                                              <>
                                                <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Unlinking...
                                              </>
                                            ) : (
                                              <>
                                                <Unlink className="w-3 h-3 mr-1" />
                                                Unlink
                                              </>
                                            )}
                                          </button>
                                        </div>
                                      )}
                                      {record.status === 'Broken' && (
                                        <div className="flex gap-2 justify-end">
                                          {resolveBrokenReason(record.broken_reason).action === 'restore' ? (
                                            <button
                                              onClick={() => showRestoreConfirmationDialog(record, customer)}
                                              disabled={restoringHistoryId === record.id}
                                              className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-full transition-all duration-200 ${
                                                restoringHistoryId === record.id
                                                  ? 'bg-orange-200 text-orange-700 cursor-not-allowed'
                                                  : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                              }`}
                                            >
                                              {restoringHistoryId === record.id ? (
                                                <>
                                                  <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-orange-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                  </svg>
                                                  Sending...
                                                </>
                                              ) : (
                                                <>
                                                  <RefreshCw className="w-3 h-3 mr-1" />
                                                  Restore
                                                </>
                                              )}
                                            </button>
                                          ) : (
                                            <button
                                              onClick={() => showNewLinkConfirmationDialog({
                                                id: record.id,
                                                customer_first_name: customer.firstName,
                                                customer_last_name: customer.lastName,
                                                customer_email: customer.email,
                                                law_firm: customer.lawFirm
                                              })}
                                              disabled={reissuingBrokenLinkId === record.id}
                                              className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-full transition-all duration-200 ${
                                                reissuingBrokenLinkId === record.id
                                                  ? 'bg-blue-200 text-blue-700 cursor-not-allowed'
                                                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                              }`}
                                            >
                                              {reissuingBrokenLinkId === record.id ? (
                                                <>
                                                  <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-blue-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                  </svg>
                                                  Sending...
                                                </>
                                              ) : (
                                                <>
                                                  <Send className="w-3 h-3 mr-1" />
                                                  Send new access link
                                                </>
                                              )}
                                            </button>
                                          )}
                                          <button
                                            onClick={() => showUnlinkConfirmationDialog(record, customer)}
                                            disabled={unlinkingHistoryId === record.id}
                                            className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-full transition-all duration-200 ${
                                              unlinkingHistoryId === record.id
                                                ? 'bg-red-200 text-red-700 cursor-not-allowed'
                                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                                            }`}
                                          >
                                            {unlinkingHistoryId === record.id ? (
                                              <>
                                                <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Unlinking...
                                              </>
                                            ) : (
                                              <>
                                                <Unlink className="w-3 h-3 mr-1" />
                                                Unlink
                                              </>
                                            )}
                                          </button>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination Controls */}
        {!loading && !error && customers.length > 0 && (
          <div className="px-6 py-4">
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSize={pageSize}
              pageSizeOptions={[10, 15, 20]}
              showInfo={true}
            />
          </div>
        )}
      </div>
      
      {/* Confirmation Modal for Send Link */}
      {showCustomerConfirmation && customerToSendLink && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Confirm Send Link
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to send an access link to <strong>{customerToSendLink.firstName} {customerToSendLink.lastName}</strong> at <strong>{customerToSendLink.email}</strong>?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCustomerConfirmation(false);
                  setCustomerToSendLink(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowCustomerConfirmation(false);
                  sendLinkToExistingCustomer(customerToSendLink);
                  setCustomerToSendLink(null);
                }}
                className="px-4 py-2 bg-brand-600 text-white hover:bg-brand-700 rounded-full transition-all duration-200"
              >
                Send Link
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Confirmation Modal for Unlink */}
      {showUnlinkConfirmation && historyToUnlink && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Confirm Unlink Account
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to unlink the account for <strong>{historyToUnlink.customer.firstName} {historyToUnlink.customer.lastName}</strong>? 
              This will remove all associated account data and transaction history.
            </p>
            <p className="text-sm text-red-600 font-medium mb-6">
              This action is irreversible. Do you want to proceed?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowUnlinkConfirmation(false);
                  setHistoryToUnlink(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  unlinkAccount(historyToUnlink.id);
                }}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-full transition-all duration-200"
              >
                Unlink Account
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Confirmation Modal for Restore Link */}
      {showRestoreConfirmation && historyToRestore && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Confirm Restore Account
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to send a restore link to <strong>{historyToRestore.customer.firstName} {historyToRestore.customer.lastName}</strong>? 
              This will allow them to re-authenticate their bank account connection.
            </p>
            <p className="text-sm text-orange-600 font-medium mb-6">
              They will receive an email with instructions to restore their account access.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRestoreConfirmation(false);
                  setHistoryToRestore(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  restoreAccount(historyToRestore, historyToRestore.customer);
                }}
                disabled={restoringHistoryId === historyToRestore?.id}
                className={`px-4 py-2 text-white rounded-full transition-all duration-200 ${
                  restoringHistoryId === historyToRestore?.id
                    ? 'bg-brand-400 cursor-not-allowed'
                    : 'bg-brand-600 hover:bg-brand-700'
                }`}
              >
                {restoringHistoryId === historyToRestore?.id ? 'Sending...' : 'Send Restore Link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Send New Access Link */}
      {showNewLinkConfirmation && brokenLinkToReissue && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Confirm Send New Access Link
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to send a new access link to <strong>{brokenLinkToReissue.customer_first_name} {brokenLinkToReissue.customer_last_name}</strong> at <strong>{brokenLinkToReissue.customer_email}</strong>?
            </p>
            <p className="text-sm text-blue-600 font-medium mb-6">
              They will receive an email with instructions to connect their bank account again.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowNewLinkConfirmation(false);
                  setBrokenLinkToReissue(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  sendNewAccessLinkForBrokenLink(brokenLinkToReissue);
                }}
                disabled={reissuingBrokenLinkId === brokenLinkToReissue?.id}
                className={`px-4 py-2 text-white rounded-full transition-all duration-200 ${
                  reissuingBrokenLinkId === brokenLinkToReissue?.id
                    ? 'bg-brand-400 cursor-not-allowed'
                    : 'bg-brand-600 hover:bg-brand-700'
                }`}
              >
                {reissuingBrokenLinkId === brokenLinkToReissue?.id ? 'Sending...' : 'Send New Access Link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Add Accounts (Same Bank) */}
      {showAddAccountsConfirmation && historyToAddAccounts && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Confirm Add Accounts
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Send {historyToAddAccounts.customer.firstName} {historyToAddAccounts.customer.lastName} a link to add accounts at their existing bank connection?
              They must keep the currently linked accounts selected; unchecking one disconnects it.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddAccountsConfirmation(false);
                  setHistoryToAddAccounts(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  addAccountsForCustomer(historyToAddAccounts);
                }}
                disabled={addingAccountsHistoryId === historyToAddAccounts?.id}
                className={`px-4 py-2 text-white rounded-full transition-all duration-200 ${
                  addingAccountsHistoryId === historyToAddAccounts?.id
                    ? 'bg-brand-400 cursor-not-allowed'
                    : 'bg-brand-600 hover:bg-brand-700'
                }`}
              >
                {addingAccountsHistoryId === historyToAddAccounts?.id ? 'Sending...' : 'Send Link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestAccess;